import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ProductGroup, StockItem } from '../components/ProductSearchInput';
import { apiFetch } from '../lib/apiFetch';
import { queryKeys } from '../lib/queryClient';

/**
 * Handler for product selection from ProductSearchInput, with TWM tier-pricing
 * resolution baked in.
 *
 * Architectural invariant (per planning/booqable-cutover-playbook.md "Pricing Authority"):
 * every line item TWM creates carries an explicit tier-derived price, NOT Booqable's
 * product default. This hook is the SOLE admin-side path for adding line items — both
 * OrderDetailsPane and the /orders/new page consume it, so tier pricing flows through
 * one resolver, server-side. The frontend resolves for display/UX; the backend
 * re-resolves on submit as the authoritative check.
 *
 * Tier follows the order's service-address municipality (NOT the customer's billing
 * address). When the order has no service address yet, the resolver returns
 * source='unresolved' and this hook surfaces a guidance message — admin UI should
 * prompt for capture rather than block silently.
 *
 * Tier D (Vieques, Culebra) returns callForPrice=true — UI must block the add and
 * surface "Call for Price — manual quote required."
 */

export interface PricingResult {
  price_in_cents: number | null;
  tier_label: string | null;
  tier_id: string | null;
  call_for_price: boolean;
  municipality_name: string | null;
  /** True once the order has a service address on file, independent of whether
   *  a tier resolved. Distinguishes "no address yet" from "address captured but
   *  unmapped" — both report source='unresolved'. */
  service_address_captured: boolean;
  size_label: string | null;
  source: 'tier_table' | 'product_default' | 'product_default_fallback' | 'unresolved';
  message: string | null;
}

export interface LineAddStartInfo {
  /** Client-generated id for the optimistic placeholder row. The success and
   *  failure callbacks echo it back so the caller can reconcile or remove the
   *  exact row it appended. */
  tempId: string;
  productGroup: ProductGroup;
  stockItem: StockItem;
  quantity: number;
  /** The in-order phase (section) this add targets, or null for a plain /
   *  Unscheduled add. Lets the caller tag the optimistic placeholder so it
   *  appears in the right phase card instead of the order's flat list. */
  sectionId: string | null;
}

export interface LineAddedInfo {
  /** Same id handed to onLineAddStart — lets the caller swap the placeholder
   *  for the real line in place rather than appending a duplicate. */
  tempId: string;
  lineId: string | null;
  productGroup: ProductGroup;
  stockItem: StockItem;
  quantity: number;
  appliedPriceInCents: number | null;
  /** The definitive per-unit price now on the created line — TWM's override
   *  when one was applied, else the product-default price Booqable booked it
   *  at (read off the create_line sideload, server-side). Lets the caller
   *  settle the optimistic row immediately instead of waiting on the refetch. */
  linePriceInCents: number | null;
  pricingSource?: PricingResult['source'];
}

interface UseProductSelectionParams {
  orderId?: string | null;
  /** When set, the added line is tagged to this in-order phase (section) and
   *  tier-priced on the SECTION's window rather than the order's. Omit for a
   *  plain (ungrouped) line. */
  sectionId?: string | null;
  /** Fired synchronously the moment a product is selected, before the add-line
   *  POST goes out — the caller appends an optimistic placeholder row so the
   *  item shows up instantly instead of after the round trip. */
  onLineAddStart?: (info: LineAddStartInfo) => void;
  onLineAdded?: (info: LineAddedInfo) => void;
  /** Fired when the add-line POST fails — the caller removes the placeholder. */
  onLineAddFailed?: (info: { tempId: string }) => void;
}

interface UseProductSelectionResult {
  handleProductSelect: (productGroup: ProductGroup, stockItem: StockItem) => Promise<void>;
  tier: string | null;
  callForPrice: boolean;
  municipalityName: string | null;
  /** True when the resolver returned source='unresolved' — the tier couldn't be
   *  derived. Gates whether the badge shows a capture/recapture prompt at all. */
  serviceAddressMissing: boolean;
  /** True once the order has a service address on file (from the resolver's
   *  explicit service_address_captured flag). When serviceAddressMissing is
   *  true, this disambiguates the prompt: false → "capture an address",
   *  true → "address captured but needs a tier mapping; recapture". */
  serviceAddressCaptured: boolean;
  /** True while the /pricing/resolve probe is in flight — the order's tier
   *  isn't confirmed yet. Callers gate the product picker on this so a line
   *  can't be added before its tier-derived price is known. */
  tierResolving: boolean;
  /** Re-probe the resolver. Call after a successful service-address capture
   *  so the tier badge populates without a page reload. */
  refetchTier: () => void;
  /** Reset the resolved tier back to the pre-capture state. Call when the
   *  order's service address is removed so the badge drops the now-stale tier
   *  and re-prompts to capture an address instead. */
  clearTier: () => void;
  isAdding: boolean;
  lastError: string | null;
  clearError: () => void;
}

export const useProductSelection = (
  params: UseProductSelectionParams = {}
): UseProductSelectionResult => {
  const { orderId, sectionId, onLineAddStart, onLineAdded, onLineAddFailed } = params;
  const queryClient = useQueryClient();

  const [tier, setTier] = useState<string | null>(null);
  const [callForPrice, setCallForPrice] = useState<boolean>(false);
  const [municipalityName, setMunicipalityName] = useState<string | null>(null);
  const [serviceAddressMissing, setServiceAddressMissing] = useState<boolean>(false);
  const [serviceAddressCaptured, setServiceAddressCaptured] = useState<boolean>(false);
  const [tierResolving, setTierResolving] = useState<boolean>(false);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [lastError, setLastError] = useState<string | null>(null);
  // Bumped externally via refetchTier() so the probe re-runs after a service
  // address capture lands. Probe-effect depends on this in addition to orderId.
  const [probeTrigger, setProbeTrigger] = useState<number>(0);

  // Pre-flight: when the order changes (or a capture lands), probe the resolver
  // for tier metadata so the UI can render a tier badge (and Tier D guardrail)
  // before any product is chosen. Returns source='unresolved' until the order
  // has a service address — that's the expected pre-capture state.
  useEffect(() => {
    if (!orderId) {
      setTier(null);
      setCallForPrice(false);
      setMunicipalityName(null);
      setServiceAddressMissing(false);
      setServiceAddressCaptured(false);
      setTierResolving(false);
      return;
    }
    let cancelled = false;
    setTierResolving(true);
    apiFetch(`/pricing/resolve?order_id=${encodeURIComponent(orderId)}`)
      .then((res) => res.json())
      .then((data: PricingResult) => {
        if (cancelled) return;
        setTier(data.tier_label);
        setCallForPrice(data.call_for_price);
        setMunicipalityName(data.municipality_name);
        setServiceAddressMissing(data.source === 'unresolved');
        setServiceAddressCaptured(!!data.service_address_captured);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to probe order tier:', err);
        setTier(null);
        setCallForPrice(false);
        setMunicipalityName(null);
        setServiceAddressMissing(false);
        setServiceAddressCaptured(false);
      })
      .finally(() => {
        if (cancelled) return;
        setTierResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, probeTrigger]);

  const refetchTier = useCallback(() => {
    setProbeTrigger((n) => n + 1);
  }, []);

  const clearTier = useCallback(() => {
    setTier(null);
    setCallForPrice(false);
    setMunicipalityName(null);
    // Drop back to the "no service address" state so the badge re-prompts for
    // capture rather than leaving the removed location's tier on screen.
    setServiceAddressMissing(true);
    setServiceAddressCaptured(false);
  }, []);

  const handleProductSelect = useCallback(
    async (productGroup: ProductGroup, stockItem: StockItem) => {
      setLastError(null);

      if (!orderId) {
        setLastError('No order yet — save the order before adding line items.');
        return;
      }

      setIsAdding(true);
      // Optimistic placeholder so the row shows up the instant the user picks,
      // not after the (multi-hop) add-line POST returns. Reconciled by
      // onLineAdded / removed by onLineAddFailed using this id.
      const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      onLineAddStart?.({ tempId, productGroup, stockItem, quantity: 1, sectionId: sectionId ?? null });
      try {
        // Prefer the variation/stock item id when available — it's the specific SKU
        // the staff picked. Fall back to the product group id for non-variation products.
        const productId = stockItem.product_id || productGroup.id;
        // Send price_each_in_cents=0 so the backend skips the unresolved/
        // call_for_price guards and creates the line at $0. Used for Tier D
        // (manual quote) AND pre-capture (no service address yet). For the
        // latter, the user captures the address and clicks "Recalculate
        // prices" on the banner that auto-appears in ProductsSection.
        const body: Record<string, unknown> = {
          product_id: productId,
          quantity: 1,
        };
        // Tag the line to its in-order phase so it's priced on the section
        // window and grouped under that section.
        if (sectionId) {
          body.section_id = sectionId;
        }
        if (callForPrice || serviceAddressMissing) {
          body.price_each_in_cents = 0;
        }
        const res = await apiFetch(
          `/booqable/orders/${encodeURIComponent(orderId)}/lines`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }
        );

        const data = await res.json();
        if (!res.ok) {
          if (res.status === 422 && data.error === 'call_for_price') {
            setLastError(data.message || 'Call for Price — manual quote required.');
          } else {
            setLastError(data.error || `Add line failed (HTTP ${res.status})`);
          }
          onLineAddFailed?.({ tempId });
          return;
        }

        // The line now consumes stock for its rental window — immediately for a
        // reserved order (Booqable auto-reserves units on add) and as planned
        // qty for a draft (which still counts against window availability).
        // Invalidate the catalog so the product picker reflects the new
        // availability right away, otherwise staff could keep adding against a
        // stale count and overbook. This is the SOLE admin add path, so every
        // consumer (OrderDetails + /orders/new) gets the refresh for free.
        queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });

        // Surface GENUINE pricing fallbacks as a visible error so staff see
        // them immediately rather than silently getting Booqable's default
        // price on the line. Only 'product_default_fallback' qualifies — a
        // roll-off that should be tier-priced but couldn't be (missing rate
        // row) or a failed price override. Plain 'product_default' is the
        // intended state for flat-fee products (Bulldog bag, front-loader)
        // that legitimately bill at their Booqable default — not an error.
        // 'unresolved' is the pre-service-address state, already signaled by
        // the orange "Capture service address to see tier" pill.
        if (data.pricing?.source === 'product_default_fallback') {
          setLastError(
            data.pricing.message ||
              'Price not tier-resolved — verify line price before billing.'
          );
        }

        onLineAdded?.({
          tempId,
          lineId: data.line_id ?? null,
          productGroup,
          stockItem,
          quantity: 1,
          appliedPriceInCents:
            data.applied_price_in_cents ?? data.pricing?.price_in_cents ?? null,
          linePriceInCents:
            data.line_price_in_cents ??
            data.applied_price_in_cents ??
            data.pricing?.price_in_cents ??
            null,
          pricingSource: data.pricing?.source ?? undefined,
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error adding line';
        setLastError(msg);
        onLineAddFailed?.({ tempId });
      } finally {
        setIsAdding(false);
      }
    },
    [orderId, sectionId, callForPrice, tier, municipalityName, serviceAddressMissing, onLineAddStart, onLineAdded, onLineAddFailed, queryClient]
  );

  const clearError = useCallback(() => setLastError(null), []);

  return {
    handleProductSelect,
    tier,
    callForPrice,
    municipalityName,
    serviceAddressMissing,
    serviceAddressCaptured,
    tierResolving,
    refetchTier,
    clearTier,
    isAdding,
    lastError,
    clearError,
  };
};
